/**
 * 画布缩略图栏：同步渲染画布 JSON（仅 scale 不同），支持选中、Delete 删除
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { cn } from '@heroui/react';
import type { Page } from '@/components/canvas/document/publicTypes';
import { CanvasThumbnailPreview } from './CanvasThumbnailPreview';

/** 水平布局缩略图：16:9，固定高度 60px */
const THUMB_HEIGHT = 60;
const THUMB_RATIO = 16 / 9;
const THUMB_WIDTH = THUMB_HEIGHT * THUMB_RATIO;

export interface CanvasThumbnailsBarProps {
  /** 水平（底栏居中）或垂直（左下角导航层） */
  layout?: 'horizontal' | 'vertical';
  className?: string;
}

/** 垂直卡片：按容器实测尺寸 contain 居中渲染页面 */
function VerticalThumbnailPreview({ page }: { page: Page }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (width > 0 && height > 0) {
        setDims((prev) =>
          prev.width === width && prev.height === height ? prev : { width, height },
        );
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="relative h-full w-full">
      {dims.width > 0 && dims.height > 0 ? (
        <CanvasThumbnailPreview
          page={page}
          width={dims.width}
          height={dims.height}
          fit="contain"
          className="absolute inset-0"
        />
      ) : null}
    </div>
  );
}

export function CanvasThumbnailsBar({
  layout = 'horizontal',
  className,
}: CanvasThumbnailsBarProps) {
  const {
    pages,
    currentPageId,
    setCurrentPageId,
    deletePage,
    clearSelection,
    selectedElementIds,
    selectedPageIds,
    bringPageToFront,
  } = useCanvas();

  // 左/右方向键切换画布
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const active = document.activeElement;
      if (
        active?.getAttribute('contenteditable') === 'true' ||
        active?.tagName === 'INPUT' ||
        active?.tagName === 'TEXTAREA'
      ) {
        return;
      }
      const idx = pages.findIndex((c) => c.id === currentPageId);
      if (idx < 0) return;
      if (e.key === 'ArrowLeft' && idx > 0) {
        e.preventDefault();
        clearSelection();
        setCurrentPageId(pages[idx - 1].id);
        (document.activeElement as HTMLElement)?.blur();
      } else if (e.key === 'ArrowRight' && idx < pages.length - 1) {
        e.preventDefault();
        clearSelection();
        setCurrentPageId(pages[idx + 1].id);
        (document.activeElement as HTMLElement)?.blur();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pages, currentPageId, setCurrentPageId, clearSelection]);

  // Delete 键删除当前画布（仅当未选中任何元素时，避免与「删除选中元素」冲突）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const active = document.activeElement;
      if (
        active?.getAttribute('contenteditable') === 'true' ||
        active?.tagName === 'INPUT' ||
        active?.tagName === 'TEXTAREA'
      ) {
        return;
      }
      // 工作区圈选页 / 元素选中时由 useKeyboardShortcuts → deleteSelectedElements 处理
      if (selectedElementIds.length > 0 || selectedPageIds.length > 0) return;
      e.preventDefault();
      deletePage(currentPageId);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    pages.length,
    currentPageId,
    deletePage,
    selectedElementIds.length,
    selectedPageIds.length,
  ]);

  const handleSelect = useCallback(
    (id: string) => {
      if (id === currentPageId) return;
      clearSelection();
      setCurrentPageId(id);
      bringPageToFront(id);
    },
    [currentPageId, setCurrentPageId, clearSelection, bringPageToFront]
  );

  if (pages.length === 0) return null;

  const isVertical = layout === 'vertical';

  return (
    <div
      className={cn(
        'flex-shrink-0',
        isVertical ? 'w-full' : 'flex justify-center py-3',
        className,
      )}
    >
      <div
        className={cn(
          'flex w-full max-w-full gap-2',
          isVertical ? 'w-full flex-col' : 'items-center px-2',
        )}
      >
        <div
          className={cn(
            isVertical ? 'w-full' : 'flex-1 overflow-x-auto overflow-y-hidden',
          )}
        >
          <div className={cn('flex gap-2', isVertical ? 'w-full flex-col items-stretch' : 'items-center')}>
            {pages.map((c, index) => {
              const isSelected = c.id === currentPageId;

              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleSelect(c.id)}
                  className={cn(
                    'rounded-lg border-1 bg-surface transition-colors',
                    'hover:border-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                    isSelected ? 'border-2 border-accent' : 'border-border',
                    isVertical ? 'aspect-video w-full shrink-0' : 'shrink-0',
                  )}
                  style={
                    isVertical ? undefined : { height: THUMB_HEIGHT, width: THUMB_WIDTH }
                  }
                  title={`页码 ${index + 1}${isSelected ? '（当前）' : ''}`}
                >
                  <div className="relative h-full w-full overflow-hidden rounded-lg">
                    {isVertical ? (
                      <VerticalThumbnailPreview page={c} />
                    ) : (
                      <CanvasThumbnailPreview
                        page={c}
                        width={THUMB_WIDTH}
                        height={THUMB_HEIGHT}
                        fit="contain"
                        className="absolute inset-0"
                      />
                    )}
                    <span className="absolute left-1 bottom-1 text-xs font-medium text-foreground/90 drop-shadow-sm">
                      {index + 1}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
